import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { SavedOrder } from "@/hooks/useOrders";

interface ReportOrder extends SavedOrder {
  customerName: string;
}

interface MonthOption {
  label: string;
  year: number;
  month: number;
}

function getMonthOptions(): MonthOption[] {
  const options: MonthOption[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({
      label: d.toLocaleDateString("ar-SA", { year: "numeric", month: "long" }),
      year: d.getFullYear(),
      month: d.getMonth(),
    });
  }
  return options;
}

function formatCurrency(n: number) {
  return n.toLocaleString("ar-SA", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ر.س";
}

function growthPct(current: number, prev: number): number {
  if (prev === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - prev) / prev) * 100);
}

function GrowthBadge({ current, prev, suffix = "" }: { current: number; prev: number; suffix?: string }) {
  const pct = growthPct(current, prev);
  if (prev === 0 && current === 0) return null;
  const isUp = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-black px-1.5 py-0.5 rounded-full ${
      isUp ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
    }`}>
      <i className={`text-[10px] ${isUp ? "ri-arrow-up-line" : "ri-arrow-down-line"}`}></i>
      {Math.abs(pct)}%{suffix}
    </span>
  );
}

async function fetchMonthOrders(year: number, month: number): Promise<ReportOrder[]> {
  const start = new Date(year, month, 1).toISOString();
  const end = new Date(year, month + 1, 1).toISOString();
  const { data, error } = await supabase
    .from("orders")
    .select("*")
    .gte("created_at", start)
    .lt("created_at", end)
    .order("created_at", { ascending: false });
  if (error || !data) return [];
  return data.map((row) => ({
    orderRef: row.order_ref,
    date: row.created_at,
    name: row.name,
    phone: row.phone,
    city: row.city,
    address: row.address,
    notes: row.notes || "",
    paymentLabel: row.payment_label,
    items: row.items,
    totalPrice: row.total_price,
    discountCode: row.discount_code || "",
    discountPercent: row.discount_percent || 0,
    discountAmount: row.discount_amount || 0,
    finalPrice: row.final_price,
    status: row.status as SavedOrder["status"],
    customerId: row.customer_id ?? undefined,
    customerName: row.name,
  }));
}

function computeStats(orders: ReportOrder[]) {
  const active = orders.filter(o => o.status !== "cancelled");
  const delivered = orders.filter(o => o.status === "delivered");
  const totalRevenue = active.reduce((s, o) => s + o.finalPrice, 0);
  const avgOrderValue = active.length ? Math.round(totalRevenue / active.length) : 0;
  const conversionRate = orders.length ? Math.round((delivered.length / orders.length) * 100) : 0;
  const totalDiscount = active.reduce((s, o) => s + o.discountAmount, 0);
  return { active, delivered, totalRevenue, avgOrderValue, conversionRate, totalDiscount, total: orders.length };
}

interface Props {
  onToast: (msg: string, type?: "success" | "error") => void;
}

export default function SalesReport({ onToast }: Props) {
  const monthOptions = getMonthOptions();

  // Primary month
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [orders, setOrders] = useState<ReportOrder[]>([]);

  // Compare mode
  const [compareMode, setCompareMode] = useState(false);
  const [compareIdx, setCompareIdx] = useState(1);
  const [compareOrders, setCompareOrders] = useState<ReportOrder[]>([]);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const selected = monthOptions[selectedIdx];
  const compareSelected = monthOptions[compareIdx];

  const loadData = useCallback(async () => {
    setLoading(true);
    const [main, comp] = await Promise.all([
      fetchMonthOrders(selected.year, selected.month),
      compareMode ? fetchMonthOrders(compareSelected.year, compareSelected.month) : Promise.resolve([]),
    ]);
    setOrders(main);
    setCompareOrders(comp);
    setLoading(false);
  }, [selected, compareSelected, compareMode]);

  useEffect(() => { loadData(); }, [loadData]);

  const stats = computeStats(orders);
  const cStats = computeStats(compareOrders);

  // Daily breakdown for chart
  const daysInMonth = new Date(selected.year, selected.month + 1, 0).getDate();
  const dailyData = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    const dayOrders = stats.active.filter(o => new Date(o.date).getDate() === day);
    const cDayOrders = compareMode
      ? cStats.active.filter(o => new Date(o.date).getDate() === day)
      : [];
    return {
      day,
      count: dayOrders.length,
      revenue: dayOrders.reduce((s, o) => s + o.finalPrice, 0),
      cRevenue: cDayOrders.reduce((s, o) => s + o.finalPrice, 0),
    };
  });
  const maxRev = Math.max(...dailyData.map(d => Math.max(d.revenue, d.cRevenue)), 1);

  // City map
  const cityMap: Record<string, { count: number; revenue: number; cCount: number; cRevenue: number }> = {};
  stats.active.forEach(o => {
    if (!cityMap[o.city]) cityMap[o.city] = { count: 0, revenue: 0, cCount: 0, cRevenue: 0 };
    cityMap[o.city].count++;
    cityMap[o.city].revenue += o.finalPrice;
  });
  if (compareMode) {
    cStats.active.forEach(o => {
      if (!cityMap[o.city]) cityMap[o.city] = { count: 0, revenue: 0, cCount: 0, cRevenue: 0 };
      cityMap[o.city].cCount++;
      cityMap[o.city].cRevenue += o.finalPrice;
    });
  }
  const citySorted = Object.entries(cityMap).sort((a, b) => b[1].revenue - a[1].revenue);

  // Payment map
  const paymentMap: Record<string, { count: number; revenue: number }> = {};
  stats.active.forEach(o => {
    const key = o.paymentLabel || "غير محدد";
    if (!paymentMap[key]) paymentMap[key] = { count: 0, revenue: 0 };
    paymentMap[key].count++;
    paymentMap[key].revenue += o.finalPrice;
  });

  // Top products
  const productMap: Record<string, { name: string; qty: number; revenue: number; image: string; cQty: number; cRevenue: number }> = {};
  stats.active.forEach(o => {
    o.items.forEach((item: { nameAr: string; qty: number; price: number; image: string }) => {
      if (!productMap[item.nameAr]) productMap[item.nameAr] = { name: item.nameAr, qty: 0, revenue: 0, image: item.image, cQty: 0, cRevenue: 0 };
      productMap[item.nameAr].qty += item.qty;
      productMap[item.nameAr].revenue += item.price * item.qty;
    });
  });
  if (compareMode) {
    cStats.active.forEach(o => {
      o.items.forEach((item: { nameAr: string; qty: number; price: number; image: string }) => {
        if (!productMap[item.nameAr]) productMap[item.nameAr] = { name: item.nameAr, qty: 0, revenue: 0, image: item.image, cQty: 0, cRevenue: 0 };
        productMap[item.nameAr].cQty += item.qty;
        productMap[item.nameAr].cRevenue += item.price * item.qty;
      });
    });
  }
  const topProducts = Object.values(productMap).sort((a, b) => b.revenue - a.revenue).slice(0, 5);

  // Status dist
  const statusDist = [
    { label: "قيد المعالجة", count: orders.filter(o => o.status === "pending").length,   color: "bg-amber-400",  text: "text-amber-700",  cCount: compareOrders.filter(o => o.status === "pending").length },
    { label: "تم التأكيد",   count: orders.filter(o => o.status === "confirmed").length, color: "bg-green-400",  text: "text-green-700",  cCount: compareOrders.filter(o => o.status === "confirmed").length },
    { label: "تم الشحن",     count: orders.filter(o => o.status === "shipped").length,   color: "bg-indigo-400", text: "text-indigo-700", cCount: compareOrders.filter(o => o.status === "shipped").length },
    { label: "تم التوصيل",   count: orders.filter(o => o.status === "delivered").length, color: "bg-teal-400",   text: "text-teal-700",   cCount: compareOrders.filter(o => o.status === "delivered").length },
    { label: "ملغي",          count: orders.filter(o => o.status === "cancelled").length, color: "bg-red-400",    text: "text-red-700",    cCount: compareOrders.filter(o => o.status === "cancelled").length },
  ].filter(s => s.count > 0 || s.cCount > 0);

  // CSV Export
  const exportCSV = useCallback(() => {
    setExporting(true);
    try {
      const headers = ["رقم الطلب","التاريخ","الوقت","اسم العميل","الجوال","المدينة","العنوان","طريقة الدفع","المنتجات","الإجمالي قبل الخصم","كود الخصم","نسبة الخصم","قيمة الخصم","الإجمالي النهائي","الحالة","ملاحظات"];
      const allOrders = compareMode ? [...orders, ...compareOrders] : orders;
      const rows = allOrders.map(o => {
        const d = new Date(o.date);
        const statusMap: Record<string, string> = { pending:"قيد المعالجة", confirmed:"تم التأكيد", shipped:"تم الشحن", delivered:"تم التوصيل", cancelled:"ملغي" };
        return [
          o.orderRef, d.toLocaleDateString("ar-SA"), d.toLocaleTimeString("ar-SA",{hour:"2-digit",minute:"2-digit"}),
          o.name, o.phone, o.city, o.address, o.paymentLabel,
          o.items.map((i: { nameAr: string; qty: number; price: number }) => `${i.nameAr} x${i.qty} (${i.price} ر.س)`).join(" | "),
          o.totalPrice, o.discountCode||"-", o.discountPercent?`${o.discountPercent}%`:"-",
          o.discountAmount||0, o.finalPrice, statusMap[o.status]||o.status, o.notes||"-"
        ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(",");
      });
      const bom = "\uFEFF";
      const csv = bom + [headers.map(h=>`"${h}"`).join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type:"text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `تقرير_${selected.label}${compareMode?`_vs_${compareSelected.label}`:""}.csv`.replace(/\s/g,"_");
      a.click();
      URL.revokeObjectURL(url);
      onToast("تم تصدير التقرير بنجاح ✓");
    } catch { onToast("حدث خطأ أثناء التصدير","error"); }
    setExporting(false);
  }, [orders, compareOrders, selected, compareSelected, compareMode, onToast]);

  return (
    <div className="flex flex-col gap-5" id="sales-report-print">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-black text-[#1a1a1a] text-lg flex items-center gap-2">
            <i className="ri-bar-chart-2-line text-amber-600"></i>
            تقرير المبيعات الشهري
          </h2>
          <p className="text-gray-400 text-xs mt-0.5">تحليل شامل للمبيعات والإيرادات</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => window.print()} className="flex items-center gap-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-bold text-sm px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-printer-line text-base"></i>
            طباعة
          </button>
          <button onClick={exportCSV} disabled={exporting || orders.length === 0} className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#333] disabled:opacity-50 text-white font-bold text-sm px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
            {exporting ? <i className="ri-loader-4-line animate-spin text-base"></i> : <i className="ri-download-2-line text-base"></i>}
            تصدير CSV
          </button>
        </div>
      </div>

      {/* ── Month Selectors ── */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Primary month */}
          <div className="flex items-center gap-2 flex-1">
            <div className="w-3 h-3 rounded-full bg-[#1a1a1a] flex-shrink-0"></div>
            <label className="text-xs font-bold text-gray-500 whitespace-nowrap">الشهر الرئيسي</label>
            <select
              value={selectedIdx}
              onChange={e => setSelectedIdx(Number(e.target.value))}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-[#1a1a1a] outline-none focus:border-gray-400 cursor-pointer bg-white"
            >
              {monthOptions.map((m, i) => (
                <option key={i} value={i}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* Compare toggle */}
          <button
            onClick={() => setCompareMode(!compareMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap border ${
              compareMode
                ? "bg-amber-400 border-amber-400 text-black"
                : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
            }`}
          >
            <i className="ri-arrow-left-right-line text-base"></i>
            {compareMode ? "إلغاء المقارنة" : "مقارنة بشهر آخر"}
          </button>

          {/* Compare month */}
          {compareMode && (
            <div className="flex items-center gap-2 flex-1">
              <div className="w-3 h-3 rounded-full bg-amber-400 flex-shrink-0"></div>
              <label className="text-xs font-bold text-gray-500 whitespace-nowrap">شهر المقارنة</label>
              <select
                value={compareIdx}
                onChange={e => setCompareIdx(Number(e.target.value))}
                className="flex-1 border border-amber-300 rounded-lg px-3 py-2 text-sm font-bold text-[#1a1a1a] outline-none focus:border-amber-400 cursor-pointer bg-amber-50"
              >
                {monthOptions.map((m, i) => (
                  <option key={i} value={i} disabled={i === selectedIdx}>{m.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Compare legend */}
        {compareMode && (
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-2 rounded-sm bg-[#1a1a1a]"></div>
              <span className="text-xs text-gray-500">{selected.label}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-2 rounded-sm bg-amber-400"></div>
              <span className="text-xs text-gray-500">{compareSelected.label}</span>
            </div>
            <span className="text-xs text-gray-400 mr-auto">الأعمدة الداكنة = الشهر الرئيسي · الذهبية = شهر المقارنة</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
          <i className="ri-loader-4-line text-3xl animate-spin"></i>
          <p className="text-sm">جاري تحميل التقرير...</p>
        </div>
      ) : (
        <>
          {/* ── KPI Cards with Growth ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Total Orders */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg">
                  <i className="ri-shopping-bag-3-line text-amber-600 text-sm"></i>
                </div>
                {compareMode && <GrowthBadge current={stats.total} prev={cStats.total} />}
              </div>
              <p className="text-2xl font-black text-[#1a1a1a]">{stats.total}</p>
              <p className="text-xs text-gray-400 mt-0.5">إجمالي الطلبات</p>
              {compareMode && (
                <p className="text-[10px] text-amber-600 mt-1 font-bold">
                  {compareSelected.label}: <span className="text-gray-500">{cStats.total}</span>
                </p>
              )}
            </div>

            {/* Revenue */}
            <div className="bg-[#1a1a1a] rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
                  <i className="ri-money-dollar-circle-line text-white text-sm"></i>
                </div>
                {compareMode && (
                  <span className={`inline-flex items-center gap-0.5 text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                    growthPct(stats.totalRevenue, cStats.totalRevenue) >= 0
                      ? "bg-green-400/20 text-green-400"
                      : "bg-red-400/20 text-red-400"
                  }`}>
                    <i className={`text-[10px] ${growthPct(stats.totalRevenue, cStats.totalRevenue) >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"}`}></i>
                    {Math.abs(growthPct(stats.totalRevenue, cStats.totalRevenue))}%
                  </span>
                )}
              </div>
              <p className="text-lg font-black text-white leading-tight">{formatCurrency(stats.totalRevenue)}</p>
              <p className="text-xs text-white/50 mt-0.5">إجمالي الإيرادات</p>
              {compareMode && (
                <p className="text-[10px] text-amber-400 mt-1 font-bold">{compareSelected.label}: {formatCurrency(cStats.totalRevenue)}</p>
              )}
            </div>

            {/* Avg Order Value */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg">
                  <i className="ri-bar-chart-box-line text-green-600 text-sm"></i>
                </div>
                {compareMode && <GrowthBadge current={stats.avgOrderValue} prev={cStats.avgOrderValue} />}
              </div>
              <p className="text-xl font-black text-[#1a1a1a]">{formatCurrency(stats.avgOrderValue)}</p>
              <p className="text-xs text-gray-400 mt-0.5">متوسط قيمة الطلب</p>
              {compareMode && (
                <p className="text-[10px] text-amber-600 mt-1 font-bold">{compareSelected.label}: <span className="text-gray-500">{formatCurrency(cStats.avgOrderValue)}</span></p>
              )}
            </div>

            {/* Conversion Rate */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg">
                  <i className="ri-percent-line text-teal-600 text-sm"></i>
                </div>
                {compareMode && <GrowthBadge current={stats.conversionRate} prev={cStats.conversionRate} suffix=" نقطة" />}
              </div>
              <p className="text-2xl font-black text-[#1a1a1a]">{stats.conversionRate}%</p>
              <p className="text-xs text-gray-400 mt-0.5">نسبة التوصيل</p>
              <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-teal-400 rounded-full transition-all" style={{ width: `${stats.conversionRate}%` }}></div>
              </div>
              {compareMode && (
                <p className="text-[10px] text-amber-600 mt-1 font-bold">{compareSelected.label}: <span className="text-gray-500">{cStats.conversionRate}%</span></p>
              )}
            </div>
          </div>

          {/* ── Compare Summary Banner ── */}
          {compareMode && (
            <div className="bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] rounded-xl p-5">
              <p className="text-white font-black text-sm mb-4 flex items-center gap-2">
                <i className="ri-arrow-left-right-line text-amber-400"></i>
                ملخص المقارنة: {selected.label} مقابل {compareSelected.label}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "الطلبات", main: stats.total, comp: cStats.total, fmt: (v: number) => `${v}` },
                  { label: "الإيرادات", main: stats.totalRevenue, comp: cStats.totalRevenue, fmt: formatCurrency },
                  { label: "متوسط الطلب", main: stats.avgOrderValue, comp: cStats.avgOrderValue, fmt: formatCurrency },
                  { label: "نسبة التوصيل", main: stats.conversionRate, comp: cStats.conversionRate, fmt: (v: number) => `${v}%` },
                ].map(item => {
                  const pct = growthPct(item.main, item.comp);
                  const isUp = pct >= 0;
                  return (
                    <div key={item.label} className="bg-white/5 rounded-xl p-3">
                      <p className="text-white/50 text-[10px] mb-2">{item.label}</p>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2 h-2 rounded-full bg-white flex-shrink-0"></div>
                        <p className="text-white text-xs font-black">{item.fmt(item.main)}</p>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"></div>
                        <p className="text-amber-400 text-xs font-bold">{item.fmt(item.comp)}</p>
                      </div>
                      <div className={`flex items-center gap-1 text-[11px] font-black ${isUp ? "text-green-400" : "text-red-400"}`}>
                        <i className={`text-xs ${isUp ? "ri-arrow-up-circle-fill" : "ri-arrow-down-circle-fill"}`}></i>
                        {isUp ? "+" : ""}{pct}%
                        <span className="text-white/30 font-normal text-[10px]">{isUp ? "نمو" : "انخفاض"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Daily Revenue Chart ── */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-bold text-[#1a1a1a]">الإيرادات اليومية</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {selected.label}{compareMode ? ` مقابل ${compareSelected.label}` : ""}
                </p>
              </div>
              <p className="text-sm font-black text-[#1a1a1a]">
                {formatCurrency(Math.max(...dailyData.map(d => d.revenue)))}
              </p>
            </div>

            <div className="flex items-end gap-0.5 h-28">
              {dailyData.map((d) => {
                const h = maxRev === 0 ? 0 : (d.revenue / maxRev) * 100;
                const ch = maxRev === 0 ? 0 : (d.cRevenue / maxRev) * 100;
                const isToday = new Date().getDate() === d.day && new Date().getMonth() === selected.month && new Date().getFullYear() === selected.year;
                return (
                  <div key={d.day} className="flex-1 flex items-end gap-px group relative" style={{ height: "100%" }}>
                    {/* Main bar */}
                    <div
                      className={`flex-1 rounded-t-sm transition-all ${isToday ? "bg-amber-400" : d.revenue > 0 ? "bg-[#1a1a1a] group-hover:bg-[#333]" : "bg-gray-100"}`}
                      style={{ height: `${Math.max(h, d.revenue > 0 ? 4 : 1)}%` }}
                    ></div>
                    {/* Compare bar */}
                    {compareMode && (
                      <div
                        className={`flex-1 rounded-t-sm transition-all ${d.cRevenue > 0 ? "bg-amber-300 group-hover:bg-amber-400" : "bg-gray-50"}`}
                        style={{ height: `${Math.max(ch, d.cRevenue > 0 ? 4 : 1)}%` }}
                      ></div>
                    )}
                    {/* Tooltip */}
                    {(d.revenue > 0 || d.cRevenue > 0) && (
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-[#1a1a1a] text-white text-[9px] px-2 py-1.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-center">
                        <p className="font-bold text-white">{d.day} {selected.label.split(" ")[0]}</p>
                        {d.revenue > 0 && <p className="text-gray-300">{formatCurrency(d.revenue)} · {d.count} طلب</p>}
                        {compareMode && d.cRevenue > 0 && <p className="text-amber-400">{formatCurrency(d.cRevenue)}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-[10px] text-gray-400">1</span>
              <span className="text-[10px] text-gray-400">{Math.round(daysInMonth / 2)}</span>
              <span className="text-[10px] text-gray-400">{daysInMonth}</span>
            </div>
          </div>

          {/* ── 3-col: Status + Cities + Payment ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Status */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-sm font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
                <i className="ri-pie-chart-line text-gray-400 text-sm"></i>
                توزيع الحالات
              </p>
              {statusDist.length === 0 ? (
                <p className="text-xs text-gray-300 text-center py-4">لا توجد طلبات</p>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {statusDist.map(s => {
                    const pct = stats.total ? Math.round((s.count / stats.total) * 100) : 0;
                    const cPct = cStats.total ? Math.round((s.cCount / cStats.total) * 100) : 0;
                    return (
                      <div key={s.label}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-bold ${s.text}`}>{s.label}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 font-bold">{s.count}</span>
                            {compareMode && s.cCount > 0 && (
                              <span className="text-[10px] text-amber-600">/ {s.cCount}</span>
                            )}
                          </div>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden relative">
                          <div className={`h-full ${s.color} rounded-full`} style={{ width: `${pct}%` }}></div>
                        </div>
                        {compareMode && s.cCount > 0 && (
                          <div className="h-1 bg-gray-50 rounded-full overflow-hidden mt-0.5">
                            <div className="h-full bg-amber-300 rounded-full" style={{ width: `${cPct}%` }}></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Cities */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-sm font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
                <i className="ri-map-pin-line text-gray-400 text-sm"></i>
                أفضل المدن
              </p>
              {citySorted.length === 0 ? (
                <p className="text-xs text-gray-300 text-center py-4">لا توجد بيانات</p>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {citySorted.slice(0, 5).map(([city, data], i) => {
                    const maxCityRev = citySorted[0][1].revenue;
                    const pct = maxCityRev ? Math.round((data.revenue / maxCityRev) * 100) : 0;
                    return (
                      <div key={city}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[10px] font-black w-4 h-4 flex items-center justify-center rounded-full ${i === 0 ? "bg-amber-400 text-black" : "bg-gray-100 text-gray-500"}`}>{i + 1}</span>
                            <span className="text-xs font-bold text-[#1a1a1a]">{city}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-500 font-bold">{data.count}</span>
                            {compareMode && data.cCount > 0 && (
                              <>
                                <span className="text-[10px] text-gray-300">/</span>
                                <span className="text-[10px] text-amber-600 font-bold">{data.cCount}</span>
                                <GrowthBadge current={data.count} prev={data.cCount} />
                              </>
                            )}
                          </div>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Payment */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-sm font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
                <i className="ri-bank-card-line text-gray-400 text-sm"></i>
                طرق الدفع
              </p>
              {Object.keys(paymentMap).length === 0 ? (
                <p className="text-xs text-gray-300 text-center py-4">لا توجد بيانات</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {Object.entries(paymentMap).map(([method, data]) => (
                    <div key={method} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 flex items-center justify-center bg-gray-50 rounded-lg flex-shrink-0">
                          <i className={`text-xs ${method.includes("تحويل")||method.includes("بنك") ? "ri-bank-line text-amber-600" : "ri-money-dollar-circle-line text-green-600"}`}></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-[#1a1a1a]">{method}</p>
                          <p className="text-[10px] text-gray-400">{data.count} طلب</p>
                        </div>
                      </div>
                      <p className="text-xs font-black text-[#1a1a1a]">{formatCurrency(data.revenue)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Top Products ── */}
          {topProducts.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <p className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
                <i className="ri-trophy-line text-amber-500 text-sm"></i>
                أكثر المنتجات مبيعاً
                {compareMode && <span className="text-xs text-gray-400 font-normal">— الأرقام الذهبية = شهر المقارنة</span>}
              </p>
              <div className="flex flex-col gap-3">
                {topProducts.map((p, i) => {
                  const maxPRev = topProducts[0].revenue;
                  const pct = maxPRev ? Math.round((p.revenue / maxPRev) * 100) : 0;
                  return (
                    <div key={p.name} className="flex items-center gap-3">
                      <span className={`text-xs font-black w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${
                        i === 0 ? "bg-amber-400 text-black" : i === 1 ? "bg-gray-300 text-gray-700" : i === 2 ? "bg-amber-700/30 text-amber-800" : "bg-gray-100 text-gray-500"
                      }`}>{i + 1}</span>
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-bold text-[#1a1a1a] truncate">{p.name}</p>
                          {compareMode && <GrowthBadge current={p.revenue} prev={p.cRevenue} />}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#1a1a1a] rounded-full" style={{ width: `${pct}%` }}></div>
                          </div>
                          <span className="text-[10px] text-gray-400 whitespace-nowrap">
                            {p.qty} قطعة
                            {compareMode && p.cQty > 0 && <span className="text-amber-500"> / {p.cQty}</span>}
                          </span>
                        </div>
                      </div>
                      <div className="text-left flex-shrink-0">
                        <p className="text-xs font-black text-[#1a1a1a]">{formatCurrency(p.revenue)}</p>
                        {compareMode && p.cRevenue > 0 && (
                          <p className="text-[10px] text-amber-600 font-bold">{formatCurrency(p.cRevenue)}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Orders Table ── */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <p className="text-sm font-bold text-[#1a1a1a] flex items-center gap-2">
                <i className="ri-file-list-3-line text-gray-400 text-sm"></i>
                تفاصيل الطلبات
                <span className="text-xs text-gray-400 font-normal">({orders.length} طلب)</span>
              </p>
              <button onClick={exportCSV} disabled={exporting || orders.length === 0} className="flex items-center gap-1.5 text-xs font-bold text-[#1a1a1a] hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40">
                <i className="ri-download-2-line text-sm"></i>
                تصدير
              </button>
            </div>
            {orders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-300">
                <i className="ri-file-chart-line text-4xl mb-3"></i>
                <p className="text-sm">لا توجد طلبات في {selected.label}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      {["رقم الطلب","التاريخ","العميل","المدينة","المنتجات","طريقة الدفع","الإجمالي","الحالة"].map(h => (
                        <th key={h} className="text-right text-[11px] font-bold text-gray-400 px-4 py-3 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o, idx) => {
                      const stMap: Record<string, { label: string; color: string; bg: string }> = {
                        pending:   { label:"قيد المعالجة", color:"text-amber-700",  bg:"bg-amber-50" },
                        confirmed: { label:"تم التأكيد",   color:"text-green-700",  bg:"bg-green-50" },
                        shipped:   { label:"تم الشحن",     color:"text-indigo-700", bg:"bg-indigo-50" },
                        delivered: { label:"تم التوصيل",   color:"text-teal-700",   bg:"bg-teal-50" },
                        cancelled: { label:"ملغي",          color:"text-red-700",    bg:"bg-red-50" },
                      };
                      const st = stMap[o.status] || stMap.pending;
                      return (
                        <tr key={o.orderRef} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${idx % 2 === 0 ? "" : "bg-gray-50/30"}`}>
                          <td className="px-4 py-3 font-mono font-bold text-[#1a1a1a] text-xs whitespace-nowrap">#{o.orderRef}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{new Date(o.date).toLocaleDateString("ar-SA",{month:"short",day:"numeric"})}</td>
                          <td className="px-4 py-3">
                            <p className="text-xs font-bold text-[#1a1a1a] whitespace-nowrap">{o.name}</p>
                            <p className="text-[10px] text-gray-400">{o.phone}</p>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{o.city}</td>
                          <td className="px-4 py-3"><p className="text-xs text-gray-600 max-w-[140px] truncate">{o.items.map((i: { nameAr: string; qty: number }) => `${i.nameAr} ×${i.qty}`).join("، ")}</p></td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{o.paymentLabel}</td>
                          <td className="px-4 py-3 font-black text-[#1a1a1a] text-xs whitespace-nowrap">{formatCurrency(o.finalPrice)}</td>
                          <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2 py-1 rounded-full ${st.bg} ${st.color} whitespace-nowrap`}>{st.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#1a1a1a]">
                      <td colSpan={6} className="px-4 py-3 text-white text-xs font-bold">الإجمالي — {stats.active.length} طلب نشط</td>
                      <td className="px-4 py-3 text-amber-400 font-black text-sm whitespace-nowrap">{formatCurrency(stats.totalRevenue)}</td>
                      <td className="px-4 py-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #sales-report-print, #sales-report-print * { visibility: visible; }
          #sales-report-print { position: absolute; top: 0; right: 0; width: 100%; }
          button { display: none !important; }
          select { border: none !important; }
        }
      `}</style>
    </div>
  );
}
